import { ArrowBack } from "@mui/icons-material";
import { Grid, Typography } from "@mui/material";
import { ApiError, ApiErrorClass } from "@readyfit-common/models";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { SaveIcon } from "src/grading/assets";
import { LoadingButton } from "src/grading/components";
import { useUploadOrgJoinPageImageMutation } from "src/store/reducers/documents/documentsApiSlice";
import {
  Organization,
  useLazyGetOrganizationQuery,
  useUpdateOrgMutation,
} from "src/store/reducers/organizations/organizationsApiSlice";
import theme from "src/theme";
import * as Yup from "yup";
import OrgIcon from "../OrgIcon/OrgIcon";
import OrgMemberList from "../OrgMembersList/OrgMembersList";
import OrgGeneralInfo from "./OrgGeneralInfo/OrgGeneralInfo";
import useStyles from "./OrgProfile.styles";
import OrgRegistration from "./OrgRegistration/OrgRegistration";

const OrgProfile = () => {
  const classes = useStyles();

  const history = useHistory();

  const { orgId } = useParams<{ orgId: string }>();

  const [getOrg, { isLoading }] = useLazyGetOrganizationQuery();

  const [organization, setOrganization] = useState<Organization>();

  const [updateOrg, { isLoading: _updateOrgLoading }] = useUpdateOrgMutation();

  const [uploadJoinPageImage] = useUploadOrgJoinPageImageMutation();

  const formik = useFormik<Organization>({
    initialValues: organization
      ? {
          ...organization,
          joinPageContent: {
            testTitle: "",
            testBody: "",
            organizationBody: "",
            organizationImageUrl: "",
          },
        }
      : {
          id: "",
          name: "",
          branch: "",
          branchName: "",
          owner: {
            name: "",
            email: "",
          },
          joinPageContent: {
            testTitle: "",
            testBody: "",
            organizationBody: "",
            organizationImageUrl: "",
          },
          memberCount: 0,
        },
    validationSchema: Yup.object().shape({
      name: Yup.string().required(),
      branch: Yup.string().required(),
    }),
    onSubmit: async (values) => {
      try {
        const res = await updateOrg({
          orgId,
          payload: {
            name: values.name,
            branch: values.branch,
            joinPageContent: values.joinPageContent
              ? {
                  testTitle: values.joinPageContent.testTitle,
                  testBody: values.joinPageContent.testBody,
                  organizationBody: values.joinPageContent.organizationBody,
                }
              : undefined,
          },
        }).unwrap();

        if (res.success) alert("Organization updated");
      } catch (error) {
        console.log(error);
      }
    },
  });

  const { values, setValues, setFieldValue, handleSubmit, errors, touched } =
    formik;

  useEffect(() => {
    const fetchData = async () => {
      const { organization: o } = await getOrg({ orgId }).unwrap();
      setOrganization(o);
      setValues(o);
    };

    fetchData();
  }, [getOrg, setValues, orgId]);

  const onFieldChange = (field: string, value: string) => {
    setFieldValue(field, value);
  };

  const handleOrgImageSelect = async (file: File) => {
    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        onFieldChange(
          "joinPageContent.organizationImageUrl",
          reader.result as string,
        );
      };

      const formData = new FormData();

      formData.append("joinPageImage", file);

      const res = await uploadJoinPageImage({ orgId, formData }).unwrap();

      if (!res.success)
        throw new ApiErrorClass({ message: "Error uploading image" });
    } catch (error) {
      const typedError = error as ApiError;
      alert(typedError.data?.message || "Error uploading image");
    }
  };

  return isLoading ? (
    <Spinner
      animation="border"
      style={{ left: "50%", top: "50%", position: "relative" }}
    />
  ) : (
    <Grid item container>
      {/* LINKS */}
      <Grid item container>
        <ArrowBack />
        <Typography
          color={"#000"}
          onClick={() => history.push("/grading/admin/organizations")}
          variant="button"
          className={classes.navigationButton}
        >
          ORGANIZATIONS
        </Typography>
        <Typography color={"#000"} variant="button">
          / ORGANIZATION PROFILE
        </Typography>
      </Grid>

      {/* BANNER */}
      <Grid item container xs={12} className={classes.bannerContainer}>
        <Grid
          container
          item
          flex={1}
          alignItems={"center"}
          ml={theme.spacing(4)}
        >
          <Grid container item flexDirection="row" alignItems="center">
            <OrgIcon organization={organization} />
            <Typography
              color={theme.palette.primary.contrastText}
              variant="h3"
              textTransform="uppercase"
              ml={theme.spacing(2)}
            >
              {organization?.name}
            </Typography>
          </Grid>
          <Grid container item flexDirection="row" mt={theme.spacing(4)}>
            <Grid item flexDirection="column" mr={theme.spacing(6)}>
              <Typography
                color={theme.palette.primary.contrastText}
                variant="subtitle1"
                textTransform="uppercase"
              >
                Owner
              </Typography>
              <Typography
                color={theme.palette.primary.contrastText}
                variant="h4"
                textTransform="uppercase"
              >
                {organization?.owner.name}
              </Typography>
            </Grid>
            <Grid item flexDirection="column" mr={theme.spacing(6)}>
              <Typography
                color={theme.palette.primary.contrastText}
                variant="subtitle1"
                textTransform="uppercase"
              >
                Email
              </Typography>
              <Typography
                color={theme.palette.primary.contrastText}
                variant="h4"
                textTransform="uppercase"
              >
                {organization?.owner.email}
              </Typography>
            </Grid>
            <Grid item flexDirection="column" mr={theme.spacing(6)}>
              <Typography
                color={theme.palette.primary.contrastText}
                variant="subtitle1"
                textTransform="uppercase"
              >
                Branch
              </Typography>
              <Typography
                color={theme.palette.primary.contrastText}
                variant="h4"
                textTransform="uppercase"
              >
                {organization?.branchName}
              </Typography>
            </Grid>
            <Grid item flexDirection="column">
              <Typography
                color={theme.palette.primary.contrastText}
                variant="subtitle1"
                textTransform="uppercase"
              >
                Members
              </Typography>
              <Typography
                color={theme.palette.primary.contrastText}
                variant="h4"
                textTransform="uppercase"
              >
                {organization?.memberCount}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* GENERAL INFO */}
      {values && (
        <OrgGeneralInfo
          organization={values}
          onFieldChange={onFieldChange}
          errors={errors}
          touched={touched}
        />
      )}

      {/* REGISTRATION PAGE */}
      {values && (
        <OrgRegistration
          joinPageContent={values.joinPageContent}
          onFieldChange={onFieldChange}
          onFileSelect={handleOrgImageSelect}
          errors={errors}
          touched={touched}
        />
      )}

      {/* BOTTOM */}
      <Grid item container className={classes.bottomContainer}>
        <LoadingButton
          variant="contained"
          size="medium"
          loading={_updateOrgLoading}
          label="Save all changes"
          startIcon={<SaveIcon />}
          onClick={() => handleSubmit()}
        />
      </Grid>

      {/* MEMBERS */}
      <Grid item container>
        <OrgMemberList orgId={orgId} />
      </Grid>
    </Grid>
  );
};

export default OrgProfile;
